
import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AlertMessage } from '../../components/alertMessage';
import BottomNavigator from '../../components/bottomNavigator';
import Loader from '../../components/loader';
import ClientDataContext from '../../contexts/clientContext';
import CompanyContext from '../../contexts/companyContext';
import LoadingContext from '../../contexts/loadingContext';
import LocationContext from '../../contexts/locationContext';
import '../../css/scrollbar.css';
import { useAppRouter } from '../../hooks/useAppRouter';
import useStorage from '../../hooks/useStorage';
import { analizeOrdersListDatetime } from '../../scripts/analizeOrdersListDatetime';
import { getCompanyData } from '../../scripts/getCompanyData';
import { getCurrentClientData } from '../../scripts/getCurrentClientData';
import { sanitizeClientSessionData } from '../../scripts/sanitizeClientSessionData';
import { tableDataController } from '../../scripts/tableDataController';
import { getUserGeolocation } from './services/getUserGeolocation';
import { validateUserGeoPosition } from './services/validateUserGeoPosition';


function SmartMenu() {
  const storage = useStorage();
  const navigate = useAppRouter();
  
  const { companyId } = useParams();
  const [searchParams] = useSearchParams()
  const locationParam = searchParams.get('locationParam') ?? null;
  const actionParam = searchParams.get('actionParam') ?? null; // converte string para boolean
  const sequenceParam = searchParams.get('sequenceParam') ?? null;
  const signatureParam = searchParams.get('signatureParam') ?? null; // assinatura do link de mesa/balcão
  const routerLocation = useLocation();
  const { companyData, setCompanyData } = useContext(CompanyContext);
  const {location, setLocation} = useContext(LocationContext);
  const {clientData, setClientData} = useContext(ClientDataContext);
  const {loading, setLoading} = useContext(LoadingContext);
  const [errorAlert, setErrorAlert] = useState(false);
  const [geolocationErrorAlert, setGeolocationErrorAlert] = useState(false);
  const [geolocationBlockedAlert, setGeolocationBlockedAlert] = useState(false);
  const [reservedTableAlert, setReservedTableAlert] = useState(false);



  useEffect(() => {

    async function syncData () {
      try {

        // CASO NÃO ENCONTRE OS PARAMETROS DA URL
        if (searchParams.size == 0) {
          navigate('not-found');
          return;
        }

        // BUSCA OS DADOS DA EMPRESA BASEADO NO LINK DA MESA
        const data = await getCompanyData({
          companyId: companyId,
          locationParam: locationParam,
          actionParam: actionParam,
          sequenceParam: sequenceParam,
          signatureParam: signatureParam
        });

        const company = data.company;

        if(!company || !company.id){
          navigate('not-found');
          return;
        }

        const localCartData = await storage.get('CART');

        if(localCartData && localCartData.companyId != data.company.id){
          storage.exclude('CART');
          storage.exclude('CLIENT_DATA');
          storage.exclude('DELIVERY_ACCOUNT_SESSION');
        }

        // CONTROLA A BUSCA DOS DADOS CLIENTE / COMANDA
        const resCommandDataFromApi = await getCurrentClientData(storage);

        // ARMAZENA O VALOR PARA REUTILIZAÇÃO
        let refClientData = resCommandDataFromApi;

        setClientData(resCommandDataFromApi);
        
        // ANALISA DIFERENÇAS ENTRE OS DADOS DE LOCALIZAÇÃO ATUAL E OS SALVOS NO CACHE
        const current_location = data.location;
        
        if (company.type === 'table') {
          if(company.latitude && company.longitude){
            const resCurrentUserPosition = await getUserGeolocation();

            if(resCurrentUserPosition){
              const valCurrentPosition = await validateUserGeoPosition({
                radius: company.radius, 
                company: {
                  latitude: company.latitude, 
                  longitude: company.longitude
                }, 
                user: resCurrentUserPosition
              });
            }
          }
          
          await handleTableController({companyId: company.id, moment_table: current_location, clientData: refClientData});
        }

        // MODIFICA O TITULO DA PÁGINA WEB PARA O NOME DA EMPRESA
        if(company.name){
          document.title = `${company.name}`;
        }

        setCompanyData(company);
        analizeOrdersListDatetime(company.id, storage);

      } catch (error) {
        console.error('ERROR - INITIALIZE SMART MENU:', error);

        if(error.response.status == 403){
          navigate('not-found');
        }

        if(error.message){
          if(error.message == 'INVALID GEOLOCATION'){
            setGeolocationErrorAlert(true);
            
          } else if (error.message == 'GEOLOCATION BLOCKED'){
            setGeolocationBlockedAlert(true);
          }
            
        } else {
          navigate('not-found');
          // setErrorAlert(true);
        }

      } finally {
        setLoading(false);
      }
    }

    syncData();
  }, []);




  const showBottom = () => {
    // Lista de rotas onde o tab navigator NÃO aparece
    // Note que usamos includes para pegar sub-rotas ou nomes parciais se necessário
    const currentPath = routerLocation.pathname;
    
    // Exemplo: se a URL for .../register ou .../product/123, não mostra
    const isDenied = 
      currentPath.includes('/register') || 
      currentPath.includes('/register/delivery') || 
      currentPath.includes('/product') || 
      currentPath.includes('/about') ||
      currentPath.includes('/reserved') ||
      currentPath.includes('/not-found');

    return !isDenied && companyData.status && companyData.type !== 'view';
  };



  const handleTableController = async ({companyId, moment_table, clientData}) => {

    try{
      const res = await tableDataController({companyId: companyId, moment_table: moment_table, clientData: clientData});
  
      if(res?.message == 'RESERVED'){
        setReservedTableAlert(true);
        return;
      }
  
      if (clientData) {
        const sanitized = sanitizeClientSessionData(res?.data);
        setClientData(sanitized);
        storage.saveWithTTL('CLIENT_DATA', sanitized, 14400);
      }
  
      setLocation(moment_table);
      return;

    } catch (e) {
      console.error('ERROR - TABLE CONTROLLER:', e);
      throw e;
    }

  }


  if(loading) return <Loader/>

  if(errorAlert) return <AlertMessage MODE="text" TITLE="Sentimos muito" TEXT={`Parece que tivemos um problema em carregar as informações do estabelecimento!\n\nTente recarregar a página, ler novamente o QR-Code ou recorra a um colaborador para te auxiliar!`} />

  if(geolocationErrorAlert) return <AlertMessage MODE="text" TITLE="Localização inválida" TEXT={`Parece que você não está dentro dos limites do estabelecimento. Caso haja algum erro, peça ajuda a algum colaborador do estabelecimento!`} />

  if(geolocationBlockedAlert) return <AlertMessage MODE="text" TITLE="Ative a localização" TEXT={`Para acessar o cardápio desta mesa, você precisa liberar o acesso do SofChef a sua localização atual!\nSe mesmo com a localização ativada, você não conseguir acessar o cardápio digital, peça ajuda a um colaborador do estabelecimento!`} />

  if(reservedTableAlert) return <AlertMessage MODE="text" TITLE="Mesa reservada" TEXT={`A mesa em questão está reservada. Caso seja você o cliente no qual realizou a reserva, recorra a um(a) colaborador(a) para que ele(a) possa liberá-la para uso!`} />

  


  // SCREEN =======================================================================>
  return (
    <div className="app-container">
      {/* AQUI É A MÁGICA: 
          O <Outlet /> será substituído pelo componente da rota filha 
          (Menu, Cart, Product...) dependendo da URL.
      */}
      <Outlet />

      {/* Renderiza a barra inferior se a lógica permitir */}
      {showBottom() && <BottomNavigator />}
    </div>
  );
}

export default SmartMenu;
